import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient, UseQueryResult } from 'react-query';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom';

import { AppError } from '../../../../../framework/models/errors';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import { useGetAttachedFile } from '../../../../hooks/queries/attachedFile';
import { useGetExpense } from '../../../../hooks/queries/expense';
import { MATTER_SUMMARY_CONTEXT_NAMES } from '../../../../hooks/queries/matterSummary';
import useToastHook from '../../../../hooks/ToastHook';
import { EngeneFileObject } from '../../../../models/EngeneFileObject';
import { ExpenseGetResult } from '../../../../models/gen/expenseGetResult';
import { FileKind } from '../../../../models/gen/fileKind';
import { MatterSummary } from '../../../../models/gen/matterSummary';
import { Formatter } from '../../../../utils/FormatUtils';
import Wireframe from '../../../wireframe/Wireframe';
import { DetailState } from './Detail';

/**
 * 経費詳細画面パラメータ
 */
export type ExpenseDetailParams = {
  /**
   * 経費ID
   */
  expenseId: string;
};

export type ExpenseDetailContext = {
  expense: ExpenseGetResult;
  refetch: UseQueryResult['refetch'];
};

/**
 * 経費詳細画面
 * @returns
 */
const ExpenseDetail: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const { expenseId } = useParams<ExpenseDetailParams>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useEngeneNavigate();
  const { toast } = useToastHook();
  const { data, error, isError, isLoading, refetch } = useGetExpense({
    id: parseInt(expenseId!),
  });
  const getAttachedFile = useGetAttachedFile();
  const form = useForm<DetailState>();
  React.useEffect(() => {
    if (TypeUtils.isNonNilString(expenseId)) {
      refetch();
    }

    return () => {
      queryClient.setQueryData<Array<MatterSummary>>(
        MATTER_SUMMARY_CONTEXT_NAMES.POST_MATTER_SUMMARY_SEARCH,
        []
      );
      actions.updatePageInfo({ MATTER_EXPENSE: { matterSummary: undefined } });
    };
  }, []);
  React.useEffect(() => {
    if (!isLoading) {
      const attachedFiles = data?.result?.attachFiles;
      const formValue = {
        ...data?.result,
        expense: {
          ...data?.result?.expense,
          date: Formatter.date(
            data?.result?.expense?.date || searchParams.get('date'),
            false,
            undefined
          ),
          matterDate: Formatter.date(
            data?.result?.expense?.matterDate || searchParams.get('date'),
            false,
            undefined
          ),
        },
        files: [],
      };
      if (Array.isArray(attachedFiles) && attachedFiles.length > 0) {
        const queries = attachedFiles
          .filter(
            (attachedFile) =>
              TypeUtils.isNonNilNumber(attachedFile.id) &&
              TypeUtils.isNonNilString(attachedFile.fileName)
          )
          .map((attachedFile) => ({
            mode: FileKind.Receipt,
            id: attachedFile.id!,
            filename: attachedFile.fileName!,
          }));
        getAttachedFile.mutate(queries, {
          onSuccess: (results) => {
            if (TypeUtils.isNonNilArray(results)) {
              form.reset({
                ...formValue,
                files: results.map((result, idx) =>
                  EngeneFileObject.fromDownloaded(
                    result.file,
                    queries[idx].id,
                    queries[idx].filename || result.filename,
                    FileKind.Receipt
                  )
                ),
              });
            }
          },
        });
      } else {
        form.reset(formValue);
      }
    }
  }, [isLoading, data?.result]);
  React.useEffect(() => {
    if (TypeUtils.isNonNil(error)) {
      navigate();
      toast(error instanceof AppError ? error : '経費検索に失敗しました');
    }
  }, [isError]);

  return (
    <Wireframe isLoaded={!isLoading} title={pageNames.EXPENSE_DETAIL}>
      <FormProvider {...form}>
        <Outlet context={{ expense: data, refetch }} />
      </FormProvider>
    </Wireframe>
  );
};

export default ExpenseDetail;
